import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../../lib/http/ApiError';
import { useNavigate } from 'react-router-dom';
import { TablePaginationBar } from '../../../components/ui/TablePaginationBar';
import { TableTemplate } from '../../../components/ui/tableTemplate/TableTemplate';
import type { FilterState, FilterValue } from '../../../components/ui/tableTemplate/TableTemplateTypes';
import theme from '../../../theme/theme';
import { DEFAULT_TABLE_PAGE_SIZE, usePaginationState } from '../../../hooks/usePaginationState';
import { useAuth } from '../../auth/hooks/useAuth';
import { usePermissions } from '../../permissions/usePermissions';
import { UsersSearchField } from '../components/UsersSearchField';
import { AppModal } from '../../../components/ui/AppModal';
import { useNotification } from '../../notifications/hooks/useNotification';
import { ResetPasswordDialog } from '../components/UserActionDialogs/ResetPasswordDialog';
import { DeactivateUserDialog } from '../components/UserActionDialogs/DeactivateUserDialog';
import { ReactivateUserDialog } from '../components/UserActionDialogs/ReactivateUserDialog';
import { useGetUsers } from '../hooks/useGetUsers';
import { useDeleteUser } from '../hooks/useDeleteUser';
import { useUserColumns } from '../hooks/useUserColumns';
import type { User } from '../userTypes';
import { SEARCH_DEBOUNCE_MS } from '../../../shared/constants';

/** Which per-row action dialog is open, and on whom. */
type RowAction = { type: 'reset' | 'deactivate' | 'reactivate' | 'delete'; user: User } | null;

function filterToApiParams(filterState: FilterState<User>): Record<string, string> | undefined {
  const params: Record<string, string> = {};
  for (const [key, filter] of Object.entries(filterState) as [string, FilterValue][]) {
    if (!filter) continue;
    if (filter.kind === 'text' && filter.value) {
      params[`${key}[like]`] = filter.value;
    } else if (filter.kind === 'list' && key === 'is_active') {
      if (filter.values.length === 1 && filter.values[0] === 'active') params['is_active[eq]'] = 'true';
      if (filter.values.length === 1 && filter.values[0] === 'inactive') params['is_active[eq]'] = 'false';
    }
  }
  return Object.keys(params).length > 0 ? params : undefined;
}

export function UsersListPage() {
  const { t } = useTranslation('users');
  const navigate = useNavigate();
  const { username: callerUsername, role: callerRole } = useAuth();
  const { can } = usePermissions();
  const { notifySuccess, notifyError } = useNotification();
  const canModify = can('users', 'modify');
  const canDelete = can('users', 'delete');
  const { limit, setLimit } = usePaginationState(DEFAULT_TABLE_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');

  const [filterState, setFilterState] = useState<FilterState<User>>({});
  const setFilter = useCallback((key: keyof User, value: FilterValue) => {
    setFilterState(prev => ({ ...prev, [key]: value }));
  }, []);
  const clearFilter = useCallback((key: keyof User) => {
    setFilterState(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);
  const clearAllFilters = useCallback(() => setFilterState({}), []);

  const trimmedSearch = searchInput.trim();
  const [query, setQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setQuery(trimmedSearch), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [trimmedSearch]);
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (value.trim() === '') setQuery('');
  };

  const filterKey = `${query}|${JSON.stringify(filterState)}`;
  const [pageState, setPageState] = useState({ page: 1, key: filterKey });
  if (pageState.key !== filterKey) setPageState({ page: 1, key: filterKey });
  const page = pageState.key === filterKey ? pageState.page : 1;
  const setPage = (next: number) => setPageState({ page: next, key: filterKey });

  const { data, meta, loading, error, refetch } = useGetUsers({
    page,
    limit,
    sortBy: 'last_login_at',
    sortOrder: 'desc',
    query: query || undefined,
    filters: filterToApiParams(filterState),
  });

  const { deleteUser, loading: deleting } = useDeleteUser();

  const [rowAction, setRowAction] = useState<RowAction>(null);
  const closeDialog = () => setRowAction(null);
  const onStatusMutationSuccess = () => {
    if (page > 1) {
      setPage(1);
      return;
    }
    refetch();
  };

  const users = data ?? [];
  const paginationMeta = meta ?? {
    total: users.length,
    page,
    limit,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  };

  const feedbackState = error ? (
    <Box sx={{ p: 2 }}>
      <Alert severity="error">{t('users:table.loadError')}</Alert>
    </Box>
  ) : loading ? (
    <></>
  ) : undefined;

  const emptyStateMessage = query ? t('users:search.noResults') : t('users:table.empty');

  const handleDelete = async () => {
    if (!rowAction || rowAction.type !== 'delete') return;
    const result = await deleteUser(rowAction.user.id);
    closeDialog();
    if (result === true) {
      notifySuccess(t('delete.success'));
    } else if (ApiError.isApiError(result) && result.isClientError) {
      notifyError(t(result.statusCode === 409 ? 'delete.activeError' : 'delete.error'));
    }
    onStatusMutationSuccess();
  };

  const rowActions = useMemo(
    () => ({
      canModify,
      canDelete,
      callerRole,
      callerUsername,
      onEdit: (u: User) => navigate(`/my-ngo/users/${u.id}/edit`),
      onResetPassword: (u: User) => setRowAction({ type: 'reset', user: u }),
      onDeactivate: (u: User) => setRowAction({ type: 'deactivate', user: u }),
      onReactivate: (u: User) => setRowAction({ type: 'reactivate', user: u }),
      onDelete: (u: User) => setRowAction({ type: 'delete', user: u }),
    }),
    [canModify, canDelete, callerRole, callerUsername, navigate],
  );
  const columns = useUserColumns(rowActions);

  return (
    <Box
      sx={{
        bgcolor: theme.palette.grey[50],
        height: `calc(100vh - ${theme.toolbar.height}px)`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 3,
          py: 2,
          background: alpha(theme.palette.primary.main, 0.06),
          color: theme.palette.text.body,
        }}
      >
        <Box
          sx={{
            width: theme.avatar.md,
            height: theme.avatar.md,
            borderRadius: theme.borderRadius.md,
            background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
            flexShrink: 0,
          }}
        >
          <PersonIcon sx={{ color: theme.palette.common.white, fontSize: theme.iconSize.md }} />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: theme.typography.employeeName.fontSize,
              fontWeight: theme.typography.fontWeightMedium,
              color: theme.palette.text.body,
            }}
          >
            {t('users:pageTitle')}
          </Typography>
        </Box>

        <Box sx={{ flex: 1 }} />
        {can('users', 'create') && (
          <Button
            variant="contained"
            size="medium"
            startIcon={<AddIcon />}
            onClick={() => navigate('/my-ngo/users/create')}
            aria-label={t('users:list.create')}
            sx={{ borderRadius: 5, textTransform: 'none' }}
          >
            {t('users:list.create')}
          </Button>
        )}
      </Box>
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 4,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <TableTemplate<User>
            columns={columns}
            rows={users}
            loading={loading}
            striped
            stickyHeader
            fillHeight
            filterState={filterState}
            onFilterChange={setFilter}
            onFilterClear={clearFilter}
            onFilterClearAll={clearAllFilters}
            onRowClick={user => navigate(`/my-ngo/users/${user.id}/edit`)}
            toolbarActions={<UsersSearchField value={searchInput} onChange={handleSearchChange} />}
            emptyStateMessage={emptyStateMessage}
            errorState={feedbackState}
            pagination={
              <TablePaginationBar
                meta={paginationMeta}
                page={page}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={setLimit}
              />
            }
          />
        </Box>
      </Box>

      {rowAction?.type === 'reset' && (
        <ResetPasswordDialog user={rowAction.user} onClose={closeDialog} onSuccess={refetch} />
      )}
      {rowAction?.type === 'deactivate' && (
        <DeactivateUserDialog
          user={rowAction.user}
          onClose={closeDialog}
          onSuccess={onStatusMutationSuccess}
        />
      )}
      {rowAction?.type === 'reactivate' && (
        <ReactivateUserDialog
          user={rowAction.user}
          onClose={closeDialog}
          onSuccess={onStatusMutationSuccess}
        />
      )}

      {rowAction?.type === 'delete' && (
        <AppModal
          open
          onClose={closeDialog}
          maxWidth="xs"
          titleId="delete-user-title"
          icon={<WarningAmberOutlinedIcon sx={th => ({ fontSize: th.iconSize.sm })} />}
          title={t('delete.confirmTitle')}
          closeLabel={t('delete.cancel')}
        >
          <DialogContent sx={{ pt: 2 }}>
            <Typography>
              {t('delete.confirmMessage', {
                name: rowAction.user.display_name ?? rowAction.user.username,
              })}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            <Button variant="outlined" onClick={closeDialog} disabled={deleting}>
              {t('delete.cancel')}
            </Button>
            <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
              {t('delete.confirm')}
            </Button>
          </DialogActions>
        </AppModal>
      )}
    </Box>
  );
}
